/*
 * Drawer — 우측 슬라이드오버 패널. Modal(.modal-box, 중앙정렬 420px 고정)과 달리
 * 화면 우측에서 슬라이드인하고 폭을 자유롭게 지정해야 해서(현장 작업물 슬라이드오버,
 * FR-13 "크게보기" 폭 가변 등) 손으로 작성했다 — SiteDetailPanel.tsx의 DQ-C03 예외
 * (커스텀 오버레이 + ESC·포커스트랩) 패턴을 그대로 따른다. 헤더는 내용까지 전부
 * children이 책임진다(Modal도 title 없이 쓰면 동일 — 이 컴포넌트는 title조차 없음).
 */
import React, { useEffect, useRef, useState } from 'react';
import { cx } from '../cx';
import { useDialog } from '../useDialog';

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  /** 패널 폭(px 또는 임의 CSS width 값). 기본 480px. */
  width?: number | string;
  /** "크게보기" 토글 버튼 노출 여부(FR-13). width가 실제로 무엇을 의미하는지는 호출부가
   *  결정한다(예: viewport - LeftNav 폭) — Drawer는 두 폭 사이를 토글하는 것만 담당. */
  maximizable?: boolean;
  /** 크게보기 상태일 때 폭. maximizable=true일 때만 쓰인다. */
  maximizedWidth?: number | string;
  /** 제어형으로 쓰려면 지정 — 생략 시 내부 state로 비제어 관리. */
  maximized?: boolean;
  onMaximizedChange?: (maximized: boolean) => void;
  children: React.ReactNode;
}

export function Drawer({
  open, onClose, width = 480, maximizable = false, maximizedWidth, maximized: maximizedProp,
  onMaximizedChange, children, className = '', ...props
}: DrawerProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [maximizedState, setMaximizedState] = useState(false);
  const maximized = maximizedProp ?? maximizedState;
  const toggleMaximized = () => {
    const next = !maximized;
    if (maximizedProp === undefined) setMaximizedState(next);
    onMaximizedChange?.(next);
  };
  // 닫혔다 다시 열릴 때는 항상 기본 폭으로 리셋(비제어 모드에서만 — 제어형은 호출부 책임).
  useEffect(() => {
    if (!open && maximizedProp === undefined) setMaximizedState(false);
  }, [open, maximizedProp]);

  // 포커스 트랩·Esc·포커스 복원·겹침 처리·바깥 클릭 닫기 (Modal.tsx와 동일 훅)
  const { scrimProps } = useDialog(open, onClose, boxRef);

  if (!open) return null;

  const effectiveWidth = maximizable && maximized ? (maximizedWidth ?? width) : width;

  return (
    <div
      className="fixed inset-0 z-overlay flex justify-end"
      style={{ background: 'var(--overlay-scrim)' }}
      {...scrimProps}
    >
      <div
        className={cx('relative flex h-full flex-col overflow-hidden bg-surface-raised shadow-xl outline-none transition-[width] duration-150', className)}
        style={{ width: effectiveWidth, animation: 'drawer-slide-in .22s ease-out' }}
        {...props}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={boxRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {maximizable && (
          <button
            type="button"
            onClick={toggleMaximized}
            title={maximized ? '원래 크기로' : '크게보기'}
            aria-label={maximized ? '원래 크기로' : '크게보기'}
            className="absolute right-14 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-sm text-text-muted hover:bg-surface-subtle hover:text-text"
          >
            {maximized ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
        )}
        {children}
      </div>
      <style>{'@keyframes drawer-slide-in{from{transform:translateX(100%)}to{transform:translateX(0)}}'}</style>
    </div>
  );
}
